import React from 'react';
import CardLayoutTheme from '../theme/CardLayoutTheme'; // Adjusted path

interface CategoryLayoutProps<T extends { id: number; name: string }> {
  title: string;
  categories: T[];
  /** Optional footer content in the sidebar (e.g., Add Category form) */
  renderSidebarFooter?: React.ReactNode;
  /** Optional custom renderer for each sidebar item (e.g., with action icons) */
  renderSidebarItem?: (category: T, isSelected: boolean) => React.ReactNode; // Updated signature
  /** Render the items for the selected category */
  renderItems: (category: T) => React.ReactNode;
  /** Callback to handle adding a new category */
  onAddCategory?: (category: { name: string; description: string }) => void;
  /** Flag to indicate if this is a customer view */
  isCustomer?: boolean;
  /** Vendor ID - used in some views but not directly by CardLayoutTheme */
  vendorId?: string;
  // vendorId prop is not part of CardLayoutTheme and seems specific to Menu.tsx and AdminVendorMenu.tsx
  // If it's needed by renderItems or other custom renderers, it should be part of the closure of those functions
  // or CardLayoutTheme would need to be modified to accept it and pass it down.
}

/**
 * Responsive layout with a category pane and item area.
 * This component now delegates its rendering to CardLayoutTheme.
 */
function CategoryLayout<T extends { id: number; name: string }>(props: CategoryLayoutProps<T>) {
  const {
    title,
    categories,
    renderSidebarFooter,
    renderSidebarItem,
    renderItems,
    onAddCategory,
    isCustomer = false, // Default to false (vendor view) if not specified
  } = props;

  // The core layout logic, including category selection, dark mode, and add category form
  // (if onAddCategory is provided), is now handled by CardLayoutTheme.
  // CategoryLayout simply passes the props through.

  return (
    <CardLayoutTheme
      title={title}
      categories={categories}
      renderItems={renderItems}
      renderSidebarFooter={renderSidebarFooter}
      renderSidebarItem={renderSidebarItem}
      onAddCategory={onAddCategory}
      isCustomer={isCustomer}
    />
  );
}

export default CategoryLayout;
